import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { WebappRoutes } from "@/shared/lib/webapp-routes"

import ReadyHero from "@/assets/img/today/reports/aura_scan_ready_hero.webp"

const CLOSE_MS = 300

/**
 * First-open sheet when a ready Aura Scan report is tapped (Figma
 * Aura_Scan_Report_Ready_Popover). CTA opens the stub in-app page.
 */
export function AuraScanReadyPopover({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [closing, setClosing] = useState(false)

  const close = () => {
    if (closing) return
    setClosing(true)
    window.setTimeout(onClose, CLOSE_MS)
  }

  const open = () => {
    navigate({ to: WebappRoutes.AURA_SCAN })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[1150]">
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className={cn(
          "absolute inset-0 h-full w-full bg-dark-base/80 backdrop-blur-sm",
          closing ? "astro-sheet-fade-out" : "astro-sheet-fade",
        )}
      />

      <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-app">
        <div
          data-slot="aura-scan-ready-popover"
          className={cn(
            "relative flex flex-col items-center gap-4 overflow-hidden rounded-t-3xl bg-dark-base py-4",
            "shadow-[0_0_16px_rgba(255,83,127,0.6),0_0_32px_#9565fd]",
            closing ? "astro-sheet-down" : "astro-sheet-up",
          )}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="absolute right-4 top-4 z-10 flex size-6 items-center justify-center text-purple-light"
          >
            <X className="size-6" />
          </button>

          <img src={ReadyHero} alt="" className="h-[290px] w-[382px] object-cover" />

          <div className="flex w-[382px] flex-col items-center gap-4 px-0">
            <div className="flex w-full flex-col items-center gap-2 text-center">
              <h2 className="font-display text-2xl font-medium leading-tight text-purple-light">
                Your Aura Scan Report
                <br />
                Is Ready to Reveal
              </h2>
              <p className="font-body text-base leading-snug text-purple-light/70">
                Uncover the deeper patterns and qualities reflected in your personal energy
              </p>
            </div>
            <button
              type="button"
              onClick={open}
              className="h-14 w-full rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
            >
              Open My Aura Scan
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
